/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

import {
  updateProgrammedCalendarItem,
  updateSavedWorkoutItem,
  setCombineWithInfo,
  swapProgrammedItemOnChildCalendar,
} from '../../ducks/calendarActions';
import { getDebouncedHandlerForTable, flushDebounceForTable } from './hooks/debounceHandler';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 5px;
`;

const RestTimeInput = styled('input')`
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  min-height: 34px;
  max-height: 34px;
  background: #FCFCFC;
  padding-left: 16px;
  color: #424242;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;


   &::placeholder {
    font-family: 'Nunito Sans', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #9E9E9E;
  }
`;

const RestTimeTable = ({
  contentIndex,
  content,
  nonSwappedItemFromParentCalendar,
  date,
  dayNumber,
  isRecurring,
}) => {
  const [restTime, setRestTime] = useState('');

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const combineWithInfo = useSelector((state) => state.calendar.data.combineWithInfo);
  const programType = useSelector((state) => state.calendar.data.programType);

  useEffect(() => {
    if (content.restTime) {
      setRestTime(content.restTime);
    }
  }, [content]);

  const dispatch = useDispatch();

  const debouncedHandleUpdateRestTime = getDebouncedHandlerForTable(content.assignedId, (
    dbRestTime,
    dbContent,
    dbNonSwappedItemFromParentCalendar,
    reorder = false,
  ) => {
    if (dbRestTime === '') return; /** bail early on empty */
    const updateItem = {
      assignedId: dbContent.assignedId,
      date: programType === 'free-form-program' ? '' : moment(effectiveDate).format('YYYY-MM-DD'),
      description: dbContent?.exercise?.description || '',
      restTime: dbRestTime,
      type: 'rest',
    };
    if (programType === 'free-form-program') {
      updateItem.dayNumber = dayNumber;
    }
    if (programType !== 'free-form-program') {
      updateItem.isRecurring = isRecurring;
    }
    if (dbContent.groupingLetter) {
      updateItem.groupingLetter = dbContent.groupingLetter;
    }
    if (dbContent.groupingColorCode) {
      updateItem.groupingColorCode = dbContent.groupingColorCode;
    }
    if (programType === 'free-form-program') {
      dispatch(updateSavedWorkoutItem(currentUser.accountCode, updateItem, contentIndex, dayNumber, reorder, {
        onSuccess: () => {
          setRestTime('');
        },
        onError: () => {},
      }));
    }
    if (programType === 'calendar') {
      if (dbNonSwappedItemFromParentCalendar) {
        dispatch(swapProgrammedItemOnChildCalendar(currentUser.accountCode, updateItem, contentIndex, moment(effectiveDate).format('YYYY-MM-DD'), reorder, {
          onSuccess: () => {
            setRestTime('');
          },
          onError: () => {},
        }));
      } else {
        dispatch(updateProgrammedCalendarItem(currentUser.accountCode, updateItem, contentIndex, moment(effectiveDate).format('YYYY-MM-DD'), reorder, {
          onSuccess: () => {
            setRestTime('');
          },
          onError: () => {},
        }));
      }
    }
  }, nonSwappedItemFromParentCalendar ? 1000 : 4000);

  useEffect(() => {
    const isSource = combineWithInfo?.source?.length
      ? combineWithInfo.source.some((item) => item?.assignedId.toString() === content?.assignedId.toString())
      : false;

    const sourceItem = combineWithInfo?.source?.find(
      (item) => item?.assignedId.toString() === content?.assignedId.toString(),
    );

    const isTarget = combineWithInfo?.target?.assignedId === content?.assignedId;

    const targetItem = combineWithInfo?.target;

    if (!content.preSave && combineWithInfo && ((isSource) || (isTarget))) {
      dispatch(setCombineWithInfo(null));
      const updateItem = {
        ...content,
        groupingLetter: isSource
          ? sourceItem?.groupingLetter
          : isTarget
            ? targetItem?.groupingLetter
            : content?.groupingLetter,

        groupingColorCode: isSource
          ? sourceItem?.groupingColorCode
          : isTarget
            ? targetItem?.groupingColorCode
            : content?.groupingColorCode,
      };
      const reorder = true;
      debouncedHandleUpdateRestTime(restTime || content.restTime, updateItem, nonSwappedItemFromParentCalendar, reorder);
      flushDebounceForTable(content.assignedId);
    }
  }, [combineWithInfo]);

  const setRestTimeFunction = (value) => {
    setRestTime(value);
    const reorder = true;
    debouncedHandleUpdateRestTime(value, content, nonSwappedItemFromParentCalendar, reorder);
  };

  return (
    <Container>
      <RestTimeInput
        type='number'
        placeholder='Enter time in sec'
        value={restTime}
        onChange={(e) => {
          setRestTimeFunction(e.target.value);
        }}
      />
    </Container>
  );
};

RestTimeTable.propTypes = {
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
  isRecurring: PropTypes.string,
};

RestTimeTable.defaultProps = {
  date: null,
  dayNumber: null,
  isRecurring: null,
};

export default RestTimeTable;
